import { memo, useEffect, useRef, type Dispatch, type FC } from 'react';

import type { OverlayReducerAction } from '../reducer';

export type OverlayControllerProps = {
  overlayId: string;
  isOpen: boolean;
  /** @description Overlay를 닫는 함수 (exitDuration 후 자동으로 DOM에서 제거됨) */
  close: () => void;
};

export type OverlayAsyncControllerProps<T> = Omit<OverlayControllerProps, 'close'> & {
  close: (param: T) => void;
  reject: (reason?: unknown) => void;
};

export type OverlayControllerComponent = FC<OverlayControllerProps>;
export type OverlayAsyncControllerComponent<T> = FC<OverlayAsyncControllerProps<T>>;

type ContentOverlayControllerProps = {
  isOpen: boolean;
  overlayId: string;
  exitDuration: number;
  overlayDispatch: Dispatch<OverlayReducerAction>;
  controller: OverlayControllerComponent;
};

export const ContentOverlayController = memo(
  ({
    isOpen,
    overlayId,
    exitDuration,
    overlayDispatch,
    controller: Controller,
  }: ContentOverlayControllerProps) => {
    const prevIsOpen = useRef(isOpen);

    useEffect(() => {
      requestAnimationFrame(() => {
        overlayDispatch({ type: 'OPEN', overlayId });
      });
    }, [overlayDispatch, overlayId]);

    // isOpen이 true → false로 변경되면 exitDuration 후 자동 unmount
    useEffect(() => {
      if (prevIsOpen.current === true && isOpen === false) {
        const timer = setTimeout(() => {
          overlayDispatch({ type: 'REMOVE', overlayId });
        }, exitDuration);

        return () => clearTimeout(timer);
      }
      prevIsOpen.current = isOpen;
    }, [isOpen, overlayId, overlayDispatch, exitDuration]);

    return (
      <Controller
        isOpen={isOpen}
        overlayId={overlayId}
        close={() => overlayDispatch({ type: 'CLOSE', overlayId })}
      />
    );
  },
);

ContentOverlayController.displayName = 'ContentOverlayController';
